import { Link, useNavigate } from "react-router-dom";
import { Bell, Plus, User, LogOut, Settings, MessageSquare, HelpCircle, Mail, Sun, Moon, LayoutDashboard, ShieldCheck, LogIn, UserPlus, Inbox, Shield, ArrowLeftRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { useEffect, useState, useRef, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { usePolling } from "../hooks/usePolling";

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Math.floor((new Date() - new Date(dateStr)) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export default function Header() {
  const { user, logout, refresh } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const [unread, setUnread] = useState(0);
  const [unreadMsgs, setUnreadMsgs] = useState(0);
  const [captainCount, setCaptainCount] = useState(0);
  const [profileOpen, setProfileOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const profileRef = useRef(null);
  const bellRef = useRef(null);

  // Polls unread counts every 15s, pauses when tab is hidden.
  // Replaces the old one-time fetch — this handles both initial load and updates.
  const pollCounts = useCallback(async () => {
    if (!user) return;
    try {
      const [n, m, c] = await Promise.all([
        api.get("/notifications/unread-count"),
        api.get("/messages/unread-count"),
        api.get("/captain/jobs"),
      ]);
      setUnread(n.data.count);
      setUnreadMsgs(m.data.count);
      setCaptainCount(c.data.filter(j => !["completed", "cancelled"].includes(j.status)).length);
    } catch {}
  }, [user]);
  // Run once immediately on mount/user-change, then poll every 15s
  useEffect(() => { pollCounts(); }, [pollCounts]);
  usePolling(pollCounts, 15000, !!user);

  // Close dropdowns on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
      if (bellRef.current && !bellRef.current.contains(e.target)) setBellOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const openBell = async () => {
    setBellOpen(o => !o);
    if (!bellOpen && user) {
      try {
        const { data } = await api.get("/notifications");
        setNotifs(data);
        // Reset unread count immediately when bell is opened
        setUnread(0);
        await api.post("/notifications/read-all");
      } catch {}
    }
  };

  const markAllRead = async () => {
    await api.post("/notifications/read-all");
    setUnread(0);
    setNotifs(n => n.map(x => ({ ...x, read: true })));
  };

  return (
    <header className="sticky top-0 z-50 shell-dark border-b border-shell-border" data-testid="header">
      <div className="mx-auto max-w-7xl px-3 sm:px-6 h-16 flex items-center justify-between gap-2">
        <Link to="/" className="flex items-center shrink-0" data-testid="logo-link">
          <span className="font-display font-black text-2xl sm:text-3xl tracking-tighter gradient-text">EarnMate</span>
        </Link>

        <div className="flex items-center gap-2">
          {user?.role !== "worker" && (
            <Link to="/post-job" className="btn-primary !px-3 sm:!px-5" data-testid="post-job-btn">
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Post a Job</span>
              <span className="sm:hidden">Post</span>
            </Link>
          )}
          {user && (
            <Link to="/messages" className="icon-btn-dark relative" data-testid="messages-btn" aria-label="Messages">
              <Inbox className="w-5 h-5" />
              {unreadMsgs > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-brand text-brand-fg text-[10px] font-bold rounded-full grid place-items-center">
                  {unreadMsgs}
                </span>
              )}
            </Link>
          )}
          {user && captainCount > 0 && (
            <Link to="/captain" className="icon-btn-dark relative" data-testid="captain-btn" aria-label="Captain duties">
              <Shield className="w-5 h-5" />
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-brand text-brand-fg text-[10px] font-bold rounded-full grid place-items-center">
                {captainCount}
              </span>
            </Link>
          )}

          <div className="relative" ref={bellRef}>
            <button onClick={openBell} className="icon-btn-dark" data-testid="notif-bell-btn" aria-label="Notifications">
              <Bell className="w-5 h-5" />
              {user && unread > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-brand text-brand-fg text-[10px] font-bold rounded-full grid place-items-center" data-testid="notif-badge">
                  {unread}
                </span>
              )}
            </button>

            {bellOpen && (
              <div className="absolute right-0 mt-2 w-80 bg-popover text-popover-foreground border border-border rounded-2xl shadow-2xl overflow-hidden z-50" data-testid="notif-dropdown">
                {!user ? (
                  <div className="p-6 text-center">
                    <Bell className="w-8 h-8 mx-auto text-brand mb-2" />
                    <p className="text-sm">Sign in to see your notifications</p>
                    <Link to="/login" onClick={() => setBellOpen(false)} className="btn-primary mt-4 !px-4 !py-2 !text-xs">Sign in</Link>
                  </div>
                ) : (

                  <>
                    <div className="flex items-center justify-between p-3 border-b border-border">
                      <span className="label-eyebrow text-muted-foreground">Notifications</span>
                      <button onClick={markAllRead} className="text-xs text-brand hover:underline" data-testid="mark-all-read-btn">Mark all read</button>
                    </div>
                    <PushPrompt />

                    <div className="max-h-96 overflow-y-auto">
                      {notifs.length === 0 ? (
                        <p className="p-6 text-sm text-muted-foreground text-center">No notifications yet.</p>
                      ) : notifs.map(n => (
                        <Link key={n.id} to={n.link || "#"} onClick={() => setBellOpen(false)}
                          className={`block p-3 border-b border-border hover:bg-brand-tint ${!n.read ? "bg-brand-tint/40" : ""}`}>
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm font-medium flex-1">{n.title}</p>
                            <span className="text-[10px] text-muted-foreground whitespace-nowrap mt-0.5">{timeAgo(n.created_at)}</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.message}</p>
                        </Link>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="relative" ref={profileRef}>
            <button onClick={() => setProfileOpen(o => !o)} className="icon-btn-dark overflow-hidden" data-testid="profile-btn" aria-label="Account menu">
              {user?.avatar ? <img src={user.avatar} alt="" className="w-full h-full object-cover" /> : <User className="w-5 h-5" />}
            </button>

            {profileOpen && (
              <div className="absolute right-0 mt-2 w-64 bg-popover text-popover-foreground border border-border rounded-2xl shadow-2xl overflow-hidden z-50" data-testid="profile-dropdown">
                {user ? (
                  <div className="p-3 border-b border-border flex items-center gap-3">
                    {user.avatar
                      ? <img src={user.avatar} alt="" className="w-10 h-10 rounded-full object-cover" />
                      : <div className="w-10 h-10 rounded-full bg-brand-tint grid place-items-center"><User className="w-5 h-5 text-brand" /></div>}
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate">{user.name}</p>
                      <p className="text-xs text-muted-foreground truncate">@{user.username}</p>
                    </div>
                  </div>
                ) : (
                  <div className="p-3 border-b border-border">
                    <p className="text-sm font-semibold">Welcome to EarnMate</p>
                    <p className="text-xs text-muted-foreground">Sign in to apply or post jobs</p>
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <Link to="/login" onClick={() => setProfileOpen(false)} className="btn-tint !text-xs justify-center" data-testid="dropdown-sign-in"><LogIn className="w-3.5 h-3.5" /> Sign in</Link>
                      <Link to="/register" onClick={() => setProfileOpen(false)} className="btn-primary !text-xs justify-center !px-3" data-testid="dropdown-sign-up"><UserPlus className="w-3.5 h-3.5" /> Sign up</Link>
                    </div>
                  </div>
                )}

                {user && (
                  <div className="py-1">
                    <MenuLink to="/dashboard" icon={<LayoutDashboard className="w-4 h-4" />} label="Dashboard" onClick={() => setProfileOpen(false)} testId="goto-dashboard" />
                    <MenuLink to={`/profile/${user.username}`} icon={<User className="w-4 h-4" />} label="My profile" onClick={() => setProfileOpen(false)} />
                    <MenuLink to="/messages" icon={<Inbox className="w-4 h-4" />} label="Messages" onClick={() => setProfileOpen(false)} />
                    <MenuLink to="/notifications" icon={<Bell className="w-4 h-4" />} label="Notifications" onClick={() => setProfileOpen(false)} />
                    {user.role === "admin" && <MenuLink to="/admin" icon={<ShieldCheck className="w-4 h-4" />} label="Admin panel" onClick={() => setProfileOpen(false)} />}
                  </div>
                )}

                <div className="py-1 border-t border-border">
                  <MenuLink to="/contact" icon={<Mail className="w-4 h-4" />} label="Contact" onClick={() => setProfileOpen(false)} />
                  <MenuLink to="/faq" icon={<HelpCircle className="w-4 h-4" />} label="FAQ" onClick={() => setProfileOpen(false)} />
                  <MenuLink to="/contact" icon={<MessageSquare className="w-4 h-4" />} label="Send feedback" onClick={() => setProfileOpen(false)} />
                  <MenuLink to="/about" icon={<Settings className="w-4 h-4" />} label="About" onClick={() => setProfileOpen(false)} />
                </div>

                <div className="px-3 py-3 border-t border-border flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm">
                    {theme === "dark" ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
                    <span>{theme === "dark" ? "Dark mode" : "Light mode"}</span>
                  </div>
                  <button onClick={toggle} className={`${theme === "dark" ? "toggle-on" : ""} relative`} data-testid="theme-toggle" aria-label="Toggle theme">
                    <div className="toggle-track"><div className="toggle-thumb" /></div>
                  </button>
                </div>
                {(user.last_workspace === "events" || user.last_workspace === "freelance") && (
                  <div className="px-3 py-3 border-t border-border">
                    <div className="flex items-center gap-2 text-sm mb-2">
                      <ArrowLeftRight className="w-4 h-4" />
                      <span>Workspace</span>
                    </div>
                    <button
                      onClick={async () => {
                        const next = user.last_workspace === "events" ? "freelance" : "events";
                        await api.patch("/users/me", { last_workspace: next });
                        await refresh();
                        setProfileOpen(false);
                        nav(`/${next}`);
                        toast.success(`Switched from ${user.last_workspace === "events" ? "Events" : "Freelance"} to ${next === "events" ? "Events" : "Freelance"}`);
                      }}
                      className="relative w-full h-9 rounded-full bg-muted p-1 flex items-center"
                      data-testid="workspace-switch-btn"
                    >
                      <div
                        className="absolute top-1 bottom-1 w-[calc(50%-4px)] rounded-full bg-brand transition-transform duration-200"
                        style={{ transform: user.last_workspace === "freelance" ? "translateX(calc(100% + 8px))" : "translateX(0)" }}
                      />
                      <span className={`relative z-10 flex-1 text-center text-[10px] font-medium leading-tight transition-colors ${user.last_workspace === "events" ? "text-brand-fg" : "text-muted-foreground"}`}>
                        EarnMate Events
                      </span>
                      <span className={`relative z-10 flex-1 text-center text-[10px] font-medium leading-tight transition-colors ${user.last_workspace === "freelance" ? "text-brand-fg" : "text-muted-foreground"}`}>
                        EarnMate Freelance
                      </span>
                    </button>
                  </div>
                )}

                {user && (
                  <button onClick={async () => { setProfileOpen(false); await logout(); }}
                    className="w-full text-left px-3 py-2.5 text-sm flex items-center gap-2 border-t border-border hover:bg-brand-tint text-red-600"
                    data-testid="logout-btn">
                    <LogOut className="w-4 h-4" /> Sign out
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

function MenuLink({ to, icon, label, onClick, testId }) {
  return (
    <Link to={to} onClick={onClick} data-testid={testId} className="flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-brand-tint transition">
      <span className="text-muted-foreground">{icon}</span>
      <span>{label}</span>
    </Link>
  );
}

function PushPrompt() {
  const [show, setShow] = useState(false);

    useEffect(() => {
    if (!("Notification" in window) || !("serviceWorker" in navigator)) return;
    if (Notification.permission === "default") setShow(true);
    else if (Notification.permission === "granted") {
      navigator.serviceWorker.ready.then(async (reg) => {
        try {
          const existing = await reg.pushManager.getSubscription();
          if (existing) return;
          const sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: process.env.REACT_APP_VAPID_PUBLIC_KEY,
          });
          const api_ = (await import("../lib/api")).default;
          await api_.post("/push/subscribe", { subscription: sub.toJSON() });
        } catch (e) {
          console.error("Push subscribe error:", e);
        }
      });
    }
  }, []);

  const enable = async () => {
    const permission = await Notification.requestPermission();
    if (permission === "granted") {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: process.env.REACT_APP_VAPID_PUBLIC_KEY,
        });
        const api_ = (await import("../lib/api")).default;
        await api_.post("/push/subscribe", { subscription: sub.toJSON() });
      } catch (e) {
        console.error("Enable push error:", e);
      }
    }
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="p-3 border-b border-border bg-brand-tint/30 flex items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        <Bell className="w-4 h-4 text-brand flex-shrink-0" />
        <p className="text-xs text-foreground">Enable push notifications</p>
      </div>
      <button onClick={enable} className="text-xs btn-primary !py-1 !px-3 flex-shrink-0">Allow</button>
    </div>
  );
}